/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useTranslation } from "react-i18next";

import { type FC, type JSX, useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { MatrixClient } from "matrix-js-sdk";

import { useClientLegacy } from "../ClientContext";
import { ErrorPage, LoadingPage } from "../FullScreenView";
import { backendFetch, BackendError } from "../utils/backend";
import { widget } from "../widget";
import { RoomAccessNotice } from "../room/RoomAccessNotice";
import { guestRoomLink } from "./guestRoomLink";
import { mayUseStaffPages } from "./access";

export const StaffGate: FC<{
  children: JSX.Element;
  expectedUserId?: string;
}> = ({ children, expectedUserId }) => {
  const { t } = useTranslation();
  const { client, loading, passwordlessUser, error, confirmStaffSession } =
    useClientLegacy();
  const location = useLocation();
  const [result, setResult] = useState<{
    client: MatrixClient;
    role?: string;
    error?: Error;
  }>();
  useEffect(() => {
    if (!client || loading || client.isGuest()) return;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const verify = (): void => {
      void backendFetch(client, "/api/me")
        .then(async (response) => await response.json())
        .then((me) => {
          if (cancelled) return;
          if (me.userId !== client.getUserId())
            throw new Error(t("instance.ui.account_verification_failed"));
          if (
            (!expectedUserId || expectedUserId === me.userId) &&
            (me.role === "owner" || me.role === "organizer")
          )
            confirmStaffSession?.(me.userId);
          setResult({ client, role: me.role ?? "guest" });
        })
        .catch((cause) => {
          if (!cancelled && cause instanceof BackendError && cause.status === 429) {
            // The IP-wide minute budget can be exhausted by other tabs. Keep
            // the session and retry after the window, never bypass role checks.
            retryTimer = setTimeout(verify, 60000);
            return;
          }
          if (!cancelled)
            setResult({
              client,
              error:
                cause instanceof Error
                  ? cause
                  : new Error(t("instance.ui.account_verification_failed")),
            });
        });
    };
    verify();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [client, loading, confirmStaffSession, expectedUserId, t]);

  if (error) return <ErrorPage error={error} widget={widget} />;
  if (loading) return <LoadingPage />;
  if (
    expectedUserId &&
    (!client || client.isGuest() || client.getUserId() !== expectedUserId)
  )
    return <Navigate to={guestRoomLink(location)} replace />;
  if (!client || client.isGuest())
    return <Navigate to="/login" state={{ from: location }} replace />;
  if (result?.client !== client) return <LoadingPage />;
  if (result.error) return <ErrorPage error={result.error} widget={widget} />;
  if (!mayUseStaffPages(true, passwordlessUser, false, result.role))
    return <RoomAccessNotice title={t("instance.ui.not_staff")} />;
  return children;
};
